/* eslint-disable complexity */
import './OfferPhoneModalDesktop.css';

import React from 'react';
import QRCode from 'react-qr-code';

import classname from '@search/classname/src';
import { Modal, Tooltip, Button, Typography, useTooltip } from '@vtblife/uikit';
import { Size } from '@vtblife/uikit/legacy';
import Icon from '@vtblife/uikit-icons';

import { OfferType } from '../../../../domain/offer/search/page/OfferQuery';
import { OfferSnippetModel } from '../../../../domain/offer/search/page/SearchOffersQuery';
import { CallToActionModal } from '../CallToActionButton';
import { CircleIconWrapper, SellerIcon, SellerText } from '../CardAuthorBadge';
import { OnlinePurchase2RequestModal } from '../promo-service-banner/PromoOnlinePurchase2/OnlinePurchase2RequestModal';
import { OnlinePurchase2ConsultModal } from '../promo-service-banner/PromoOnlinePurchase2/OnlinePurchase2ConsultModal';
import { ReportModal } from '../ReportButton/ReportModal';
import { useOfferPhoneModal } from './useOfferPhoneModal';

const cn = classname.bind(null, 'OfferPhoneModalDesktop');

export function OfferPhoneModalDesktop({
    offer: offerCard,
    snippet,
    offerId,
    phoneFormatted,
    phoneHref,
    isDoppelganger,
    onClose
}: {
    offerId?: string;
    phoneHref: string;
    phoneFormatted: string;
    isDoppelganger: boolean;
    onClose(): void;
    offer?: OfferType;
    snippet?: OfferSnippetModel;
}) {
    const { to, registerControl } = useTooltip();

    const {
        showBuyOnline,
        handleCallback,
        handleNoCall,
        isReporting,
        handleReport,
        handleSuccess,
        seller,
        receivedOffer,
        newBuildingEmailExtra,
        status,
        isBuyButton,
        isNewBuilding,
        offerUrl,
        showConsultOnlinePurchase
    } = useOfferPhoneModal({ offerId, offer: offerCard, snippet });

    if (status === 'online-purchase' && receivedOffer) {
        return (
            // в карточке window.location.href будет верный, а в выдачах/похожих - нет
            <OnlinePurchase2RequestModal
                offerUrl={offerCard ? undefined : offerUrl}
                onClose={onClose}
            />
        );
    }

    if (status === 'consult-online-purchase' && receivedOffer) {
        return (
            // в карточке window.location.href будет верный, а в выдачах/похожих - нет
            <OnlinePurchase2ConsultModal
                offerUrl={offerCard ? undefined : offerUrl}
                onClose={onClose}
            />
        );
    }

    if (status === 'report' && receivedOffer) {
        return (<ReportModal
            offerId={receivedOffer.id}
            handleCloseModal={onClose}
        />);
    }

    if (status === 'call-to-action' && receivedOffer && seller) {
        return (<CallToActionModal
            newBuildingCard={receivedOffer.newBuildingCard}
            offerUrl={offerUrl}
            offerStatus={receivedOffer.status}
            onClose={onClose}
            offerLocation={receivedOffer.location}
            newBuildingEmailExtra={newBuildingEmailExtra}
            seller={seller}
            gtmSendClick='cl_showtel_modal_info_verif_silent_callback_sent'
            isNewBuilding={isNewBuilding}
        />);
    }

    const phone = (
        <a href={phoneHref} className={cn('phone', { isDoppelganger })}>{phoneFormatted}</a>
    );

    const offerButtons = (size: Size = Size.Small) => receivedOffer && (<>
        { isBuyButton && (
            <div className={cn('offer-buttons')}>
                <Button
                    size={size}
                    fullWidth
                    variant='brand-purple'
                    dataTest='buy-online'
                    onClick={showBuyOnline}
                >Заказать покупку онлайн</Button>
                <Button
                    size={size}
                    fullWidth
                    variant='secondary'
                    dataTest='consultation'
                    onClick={showConsultOnlinePurchase}
                >Консультация</Button>
            </div>
        )}
    </>);

    return (
        <Modal
            opened
            size='m'
            onClose={onClose}
        >
            <Modal.Content>
                <div className={cn('list-wrapper')}>
                    <div className={cn('list')}>
                        {isDoppelganger ? (
                            <CheckItem
                                containerRef={registerControl}
                                icon={(<>
                                    <CircleIconWrapper color='#E4FBCB'>
                                        <Icon size='m' name='shield-check' color='#78BC29' />
                                    </CircleIconWrapper>
                                    <Tooltip
                                        placement='body'
                                        to={to}
                                        direction='up'
                                    >
                                        Номер принимает только звонки, сообщения не доставляются
                                    </Tooltip>
                                </>)}
                            >
                                {phone}
                            </CheckItem>
                        ) : phone}
                        {seller && (
                            <CheckItem
                                icon={(
                                    <SellerIcon iconSrc={seller.icon} sellerType={seller.sellerType} />
                                )}
                            >
                                <SellerText
                                    organizationName={seller.organizationName}
                                    name={seller.name}
                                    sellerType={seller.sellerType}
                                />
                            </CheckItem>
                        )}

                        <CheckItem>
                            <Typography variant='primary-alone'>Скажите, что звоните по объявлению с{' '}Метра{' '}квадратного</Typography>
                        </CheckItem>
                        {isNewBuilding ? null : (
                            <CheckItem>
                                <Typography variant='primary-alone'>Никогда не переводите предоплату до{' '}осмотра</Typography>
                            </CheckItem>
                        )}
                        {isBuyButton && (
                            <CheckItem
                                icon={(
                                    <CircleIconWrapper color='hsla(251, 93%, 72%, 1)'><Icon color='white' size='m' name='house-plus' /></CircleIconWrapper>
                                )}
                            >
                                <Typography variant='primary-alone'>
                                    Безопасная сделка через&nbsp;М2, <a className={cn('link')} href='/services/deal' target='_blank'>подробнее</a>
                                </Typography>
                            </CheckItem>
                        )}
                    </div>

                    <div className={cn('qr')}>
                        <QRCode
                            value={phoneHref}
                            fgColor='#221F2E'
                            size={80}
                        />
                        <Typography
                            variant='small-alone'
                            color='secondary'
                        >
                            Наведите камеру смартфона на&nbsp;код, чтобы сразу набрать номер
                        </Typography>
                    </div>
                </div>

                {receivedOffer && offerButtons(Size.Medium)}

                <div className={cn('quest')}>
                    {status === 'success' && (<>
                        <Message
                            icon={<Icon size='m' name='thumb-up' color='#78BC29' />}
                            noMargin
                        >
                            Спасибо за ваш отзыв!
                        </Message>
                    </>)}

                    {status === 'ask-callback' && (<>
                        <Message>
                            Хотите чтобы вам перезвонили?
                        </Message>
                        <div className={cn('buttons')}>
                            <div className={cn('wide-button')}>
                                <Button
                                    size='xs'
                                    fullWidth
                                    variant='transparent'
                                    dataTest='callback'
                                    onClick={handleCallback}
                                >Да, перезвоните мне</Button>
                            </div>
                        </div>
                    </>)}

                    {status === 'error' && (<>
                        <Message noMargin>
                            Что-то пошло не так
                        </Message>
                    </>)}

                    {status === 'start' && (<>
                        <Message>
                            После звонка укажите подтвердилась&nbsp;ли информация, указанная в&nbsp;объявлении
                        </Message>

                        <div className={cn('buttons')}>
                            <Button size='xs' variant='transparent' dataTest='success'
                                onClick={handleSuccess}>Да</Button>
                            <Button size='xs' variant='transparent' dataTest='report'
                                onClick={handleReport}>Нет</Button>
                            <Button
                                size='xs'
                                fullWidth
                                variant='transparent'
                                dataTest='no-call'
                                loading={isReporting}
                                onClick={handleNoCall}
                            >Не взяли трубку</Button>
                        </div>
                    </>)}
                </div>
            </Modal.Content>
        </Modal>
    );
}

function Message({ children, icon, noMargin }: {
    icon?: React.ReactNode;
    containerRef?: React.RefCallback<HTMLElement>;
    children: React.ReactNode;
    noMargin?: boolean;
}) {
    return (
        <div className={cn('message', { noMargin })}>
            <Typography variant={icon ? 'h3' : 'secondary-alone'}>
                {children}
            </Typography>
            {icon}
        </div>
    );
}

function CheckItem({
    icon = <CircleIconWrapper><Icon size='m' name='check' /></CircleIconWrapper>,
    children,
    containerRef
}: {
    icon?: React.ReactNode;
    containerRef?: React.RefCallback<HTMLElement>;
    children: React.ReactNode;
}) {
    return (
        <div className={cn('check')} ref={containerRef}>
            {icon}
            {children}
        </div>
    );
}
